import {useStaticQuery, graphql} from 'gatsby'
import React, {FC} from 'react'

import {FooterBanner} from '@/components/footer-banner'
import {GlobalStringsWebsiteQuery} from '@/graphql-types'
import {useCopyExperiments} from '@/hooks/use-copy-experiments'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {useUser} from '@/hooks/use-user'
import GlobalStyles from '@/styles/global-styles'
import trackPageSplitExperiments from '@/utils/track-page-split-experiments'
import NotificationBanner from '@helios/shared/components/notification-banner'
import SkipToContent from '@helios/shared/components/skip-to-content'
import {useSessionTracking} from '@helios/shared/hooks/use-session-tracking'
import {CopyExperimentsProvider} from '@helios/shared/providers/copy-experiments'
import {GlobalStringsProvider} from '@helios/shared/providers/global-strings'
import {LocaleProvider} from '@helios/shared/providers/locale'
import {PageInfoProvider} from '@helios/shared/providers/page-info'
import {PageProps} from '@helios/shared/types/page-props'
import {identify} from '@helios/shared/utils/analytics'

// Don't show the notification banner on these pages
const notificationBannerOmittedPaths = [
    '/expense-policy-builder',
    '/expense-report-roi-calculator',
    '/expense-report-generator',
    '/pricing',
    '/de/bigform-partner-datev',
    '/careers/benefits',
]

type Props = Omit<PageProps, 'children'>

export const WrapPageElement: FC<Props> = ({children, ...props}) => {
    const pathnameIncludes = (path: string) => new RegExp(path).test(props.location.pathname)
    const copyExperiments = useCopyExperiments(props.pageContext.locale)
    const {isLoggedIn, hasLoggedInPreviously} = useUser()

    React.useEffect(() => {
        trackPageSplitExperiments()
        identify({
            loggedInCurrently: isLoggedIn,
            loggedInPreviously: hasLoggedInPreviously,
        })
    }, [])

    useSessionTracking({
        pathname: props.location.pathname,
    })

    const queryParams = new URLSearchParams(props.location.search)
    const hideFooterBanner = queryParams.get('hide-cookie-banner') === 'true'
    const hideNotificationBanner =
        notificationBannerOmittedPaths.some(pathnameIncludes) ||
        queryParams.get('partner') === 'datev'

    const notificationBannerContent = props.pageContext.notificationMessage
    const {allPrismicGlobalStrings} = useStaticQuery<GlobalStringsWebsiteQuery>(query)
    const [content] = useLocalizedContent(allPrismicGlobalStrings.nodes, props.pageContext.locale)
    const globalStrings = {
        ...content.data,
        notification_banner_message: content.data.notification_banner_message.raw,
        recaptcha_message: content.data.recaptcha_message.raw,
        recaptcha_and_privacy_terms: content.data.recaptcha_and_privacy_terms.raw,
        accept_terms_and_privacy: content.data.accept_terms_and_privacy.raw,
        clearbitCredits: content.data.clearbit_credits.raw,
        marketingEmailsConsentText: content.data.marketing_emails_consent_text.raw,
        yourEmailLabel: content.data.your_email_label,
        playVideo: content.data.play_video,
        localeSelectorGlobalLabel: content.data.locale_selector_global_label,
        signUp: content.data.sign_up,
        skipToContent: content.data.skip_to_content,
        consent_label: content.data.consent_label.raw,
        watchTheVideo: content.data.watch_the_video,
    }

    return (
        <>
            <GlobalStyles />

            <PageInfoProvider {...props}>
                <LocaleProvider localeCode={props.pageContext.locale}>
                    <CopyExperimentsProvider content={copyExperiments}>
                        <GlobalStringsProvider content={globalStrings}>
                            <SkipToContent />

                            {!hideNotificationBanner && (
                                <NotificationBanner content={notificationBannerContent} />
                            )}

                            {children}

                            {!hideFooterBanner && <FooterBanner />}
                        </GlobalStringsProvider>
                    </CopyExperimentsProvider>
                </LocaleProvider>
            </PageInfoProvider>
        </>
    )
}

const query = graphql`
    query GlobalStringsWebsite {
        allPrismicGlobalStrings {
            nodes {
                lang
                data {
                    notification_banner_message {
                        raw
                    }
                    close_button_text
                    visit_website
                    read_more
                    learn_more
                    show_more
                    watch_now
                    recaptcha_message {
                        raw
                    }
                    recaptcha_and_privacy_terms {
                        raw
                    }
                    show_me
                    all
                    copied_to_clipboard
                    something_went_wrong
                    first_name
                    last_name
                    work_email
                    company_name
                    company_size
                    select
                    phone_number
                    submit
                    accept_terms_and_privacy {
                        raw
                    }
                    this_field_is_required
                    must_be_company_email
                    sole_trader
                    employee
                    employees
                    your_full_name
                    your_work_email
                    please_select_number_of_employees
                    please_enter_your_work_email
                    please_enter_a_work_email
                    sticky_cta_button_text
                    play
                    admin
                    industry
                    location
                    days
                    hours
                    minutes
                    back
                    home
                    menu
                    invalid_email_error
                    loading
                    clearbit_credits {
                        raw
                    }
                    play_video
                    preview
                    mins_read
                    min_read
                    latest_articles
                    tags
                    panel
                    related_posts_text
                    sequence_posts_text
                    non_work_email_error_cta_text
                    non_work_email_error
                    country
                    locale_selector_global_label
                    marketing_emails_consent_text {
                        raw
                    }
                    your_email_label
                    sign_up
                    skip_to_content
                    consent_label {
                        raw
                    }
                    country_placeholder
                    discovered_via_label
                    discovered_via_placeholder
                    demo_submit_text
                    other
                    peer_recommendation
                    trust_pilot
                    google_search
                    youtube
                    tv
                    outdoor_advertising
                    social_media
                    press
                    first_name_error
                    last_name_error
                    phone_number_error
                    company_size_error
                    country_error
                    has_consented_error
                    get_started
                    watch_the_video
                }
            }
        }
    }
`
